
import React, { useState, useEffect } from 'react';
import { Star, Quote, BadgeCheck, ChevronLeft, ChevronRight, X, ExternalLink } from 'lucide-react';
import { REVIEWS } from '../data';
import { Review } from '../types';

export const Reviews: React.FC = () => {
  const [currentIndex, setCurrentIndex] = useState(0);
  const [selectedReview, setSelectedReview] = useState<Review | null>(null);

  // Close modal on Escape key
  useEffect(() => {
    const handleEsc = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setSelectedReview(null);
    };
    window.addEventListener('keydown', handleEsc);
    return () => window.removeEventListener('keydown', handleEsc);
  }, []);

  const nextSlide = () => {
    if (currentIndex + 3 < REVIEWS.length) {
      setCurrentIndex(currentIndex + 3);
    }
  };

  const prevSlide = () => {
    if (currentIndex - 3 >= 0) {
      setCurrentIndex(currentIndex - 3);
    } else {
      setCurrentIndex(0);
    }
  };

  const visibleReviews = REVIEWS.slice(currentIndex, currentIndex + 3);
  const showArrows = REVIEWS.length > 3;

  const TRUNCATE_LIMIT = 400;

  const isPng = (url?: string) => {
    return url?.toLowerCase().endsWith('.png');
  };

  const openReview = (review: Review) => {
    setSelectedReview(review);
  };

  return (
    <section id="reviews" className="py-24 bg-slate-950 relative overflow-hidden">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
        <div className="flex flex-col md:flex-row md:items-center justify-between mb-16 gap-4">
          <div>
            <h2 className="text-3xl md:text-4xl font-bold text-white mb-2">Client Testimonials</h2>
            <p className="text-slate-500">Feedback from real server owners.</p>
          </div>
        </div>

        <div className="relative">
          {/* Left Arrow */}
          {showArrows && (
            <button 
              onClick={prevSlide}
              disabled={currentIndex === 0}
              className={`absolute -left-4 md:-left-12 top-1/2 -translate-y-1/2 z-20 p-3 rounded-full bg-slate-900 border border-slate-700 text-white shadow-xl transition-all hover:scale-110 ${currentIndex === 0 ? 'opacity-30 cursor-not-allowed' : 'hover:border-mc-blue hover:text-mc-blue'}`}
            >
              <ChevronLeft className="w-5 h-5" />
            </button>
          )}

          <div className="grid md:grid-cols-3 gap-6">
            {visibleReviews.map((review) => {
              const isTruncated = review.text.length > TRUNCATE_LIMIT;
              const displayText = isTruncated 
                ? review.text.slice(0, TRUNCATE_LIMIT) + '...' 
                : review.text;

              return (
                <div 
                  key={review.id} 
                  onClick={() => openReview(review)}
                  className="relative flex flex-col p-8 bg-gradient-to-br from-slate-900 to-slate-950 rounded-xl border-l-4 border-mc-gold shadow-xl cursor-pointer group hover:-translate-y-1 transition-all duration-300 hover:shadow-2xl"
                >
                  {/* Hover hint */}
                  <div className="absolute top-4 right-4 opacity-0 group-hover:opacity-100 transition-opacity text-xs text-slate-500 font-mono">
                    Click to expand
                  </div>
                  
                  {/* Rating */}
                  <div className="flex gap-1 mb-4">
                     {[...Array(5)].map((_, i) => (
                        <Star 
                          key={i} 
                          className={`w-4 h-4 ${i < review.rating ? 'text-mc-gold fill-mc-gold' : 'text-slate-800'}`} 
                        />
                      ))}
                  </div>
                  
                  {/* Review Text */}
                  <div className="flex-grow mb-8 relative">
                     <Quote className="absolute -top-2 -left-2 w-6 h-6 text-slate-800 fill-current transform -scale-x-100 opacity-50" />
                     <p className="text-slate-300 text-sm leading-relaxed relative z-10 pl-4 italic">
                      "{displayText}"
                    </p>
                  </div>

                  {/* Author Footer */}
                  <div className="mt-auto flex items-center gap-3 pt-4 border-t border-slate-800/50">
                    <div className="w-8 h-8 rounded-full bg-slate-800 flex items-center justify-center text-xs font-bold text-slate-400">
                      {review.author.charAt(0).toUpperCase()}
                    </div>
                    <div className="flex flex-col">
                      <span className="font-bold text-white text-sm">{review.author}</span>
                      {review.proofUrl && (
                        <a 
                          href={review.proofUrl} 
                          target="_blank" 
                          rel="noreferrer"
                          onClick={(e) => e.stopPropagation()} // Prevent modal open when clicking link
                          className="text-[10px] text-mc-blue hover:underline flex items-center gap-1 hover:text-white transition-colors"
                        >
                          <BadgeCheck className="w-3 h-3" /> Verified Proof
                        </a>
                      )}
                    </div>
                  </div>
                </div>
              );
            })}
            
            {visibleReviews.length === 0 && (
              <div className="col-span-3 text-center py-12 bg-slate-900/30 rounded-xl border border-dashed border-slate-800 text-slate-500">
                No reviews to display currently.
              </div>
            )}
          </div>

          {/* Right Arrow */}
          {showArrows && (
            <button 
              onClick={nextSlide}
              disabled={currentIndex + 3 >= REVIEWS.length}
              className={`absolute -right-4 md:-right-12 top-1/2 -translate-y-1/2 z-20 p-3 rounded-full bg-slate-900 border border-slate-700 text-white shadow-xl transition-all hover:scale-110 ${currentIndex + 3 >= REVIEWS.length ? 'opacity-30 cursor-not-allowed' : 'hover:border-mc-blue hover:text-mc-blue'}`}
            >
              <ChevronRight className="w-5 h-5" />
            </button>
          )}
        </div>
      </div>

      {/* Expanded Review Modal */}
      {selectedReview && (
        <div className="fixed inset-0 z-[100] flex items-center justify-center p-4 bg-slate-950/90 backdrop-blur-sm animate-in fade-in duration-200">
          <div 
            className="bg-slate-900 border border-slate-800 rounded-2xl shadow-2xl w-full max-w-2xl max-h-[90vh] overflow-y-auto relative flex flex-col"
            onClick={(e) => e.stopPropagation()}
          >
            {/* Modal Header */}
            <div className="sticky top-0 bg-slate-900/95 backdrop-blur border-b border-slate-800 p-6 flex items-center justify-between z-10">
              <div className="flex items-center gap-4">
                <div className="w-10 h-10 rounded-full bg-slate-800 flex items-center justify-center text-lg font-bold text-slate-400">
                  {selectedReview.author.charAt(0).toUpperCase()}
                </div>
                <div>
                  <h3 className="font-bold text-white text-lg">{selectedReview.author}</h3>
                  <div className="flex items-center gap-2">
                     <div className="flex">
                      {[...Array(5)].map((_, i) => (
                          <Star 
                            key={i} 
                            className={`w-3 h-3 ${i < selectedReview.rating ? 'text-mc-gold fill-mc-gold' : 'text-slate-800'}`} 
                          />
                        ))}
                     </div>
                     {selectedReview.proofUrl && (
                        <a 
                          href={selectedReview.proofUrl} 
                          target="_blank" 
                          rel="noreferrer"
                          className="text-xs text-mc-blue hover:underline flex items-center gap-1"
                        >
                          <BadgeCheck className="w-3 h-3" /> Verified
                          <ExternalLink className="w-3 h-3 ml-0.5" />
                        </a>
                      )}
                  </div>
                </div>
              </div>
              <button 
                onClick={() => {
                    setSelectedReview(null);
                }}
                className="p-2 hover:bg-slate-800 rounded-full text-slate-500 hover:text-white transition-colors"
              >
                <X className="w-6 h-6" />
              </button>
            </div>

            {/* Modal Content */}
            <div className="p-8 space-y-8">
              <div className="relative">
                <Quote className="absolute -top-4 -left-4 w-8 h-8 text-slate-800 fill-current transform -scale-x-100 opacity-50" />
                <p className="text-slate-200 text-lg leading-relaxed italic whitespace-pre-wrap">
                  "{selectedReview.text}"
                </p>
              </div>

              {/* Image Proof Preview */}
              {selectedReview.proofUrl && isPng(selectedReview.proofUrl) && (
                <div className="mt-6">
                  <div className="text-xs uppercase font-bold text-slate-500 mb-2">Proof of Work</div>
                  <div className="rounded-lg overflow-hidden border border-slate-800 bg-slate-950">
                    <img 
                      src={selectedReview.proofUrl} 
                      alt={`Proof for ${selectedReview.author}`}
                      className="w-full h-auto object-contain max-h-[400px]"
                    />
                  </div>
                </div>
              )}
            </div>
          </div>
          
          {/* Click outside to close */}
          <div 
            className="absolute inset-0 z-[-1]" 
            onClick={() => {
                setSelectedReview(null);
            }}
          ></div>
        </div>
      )}
    </section>
  );
};
